import type { UploadOutcome } from '~/hooks/use-upload-with-progress'
import { z } from 'zod'

/**
 * Pourquoi l'import d'un package a échoué, ou `null` s'il a réussi (#310).
 * Annuler n'est pas un échec : `cancelled` n'a pas de cause.
 */
export type ModuleUploadError
  = | { kind: 'quota', usedBytes: number, quotaBytes: number, incomingBytes: number }
    | { kind: 'tooLarge' }
    | { kind: 'rejected', message: string }
    | { kind: 'network' }
    | { kind: 'unknown' }

/** Refus de quota (`storage-quota.exception.ts`, #180). */
const quotaRefusal = z.object({
  code: z.literal('STORAGE_QUOTA_EXCEEDED'),
  usedBytes: z.number(),
  quotaBytes: z.number(),
  incomingBytes: z.number(),
})

/** Tout autre refus Nest : son `message` dit ce qui ne va pas. */
const apiRefusal = z.object({ message: z.string().min(1) })

/**
 * Lit la réponse de `POST /modules/upload`.
 *
 * Le refus de quota (#180) porte son code et ses trois chiffres plutôt qu'une
 * phrase : c'est ce qui permet de le dire dans la langue de l'utilisateur. Un
 * 413 vient de multer (`SCORM_MAX_UPLOAD_BYTES`), quand la zone de dépôt
 * n'avait pas encore la borne. Les autres refus gardent le message de l'API,
 * qui nomme ce qui manque au package (manifeste absent, zip illisible).
 */
export function moduleUploadError(outcome: UploadOutcome): ModuleUploadError | null {
  if (outcome.kind === 'cancelled') return null
  if (outcome.kind === 'networkError') return { kind: 'network' }

  const { status, body } = outcome.response
  if (status >= 200 && status < 300) return null

  const quota = quotaRefusal.safeParse(body)
  if (quota.success) {
    const { usedBytes, quotaBytes, incomingBytes } = quota.data
    return { kind: 'quota', usedBytes, quotaBytes, incomingBytes }
  }
  if (status === 413) return { kind: 'tooLarge' }

  const refusal = apiRefusal.safeParse(body)
  return refusal.success ? { kind: 'rejected', message: refusal.data.message } : { kind: 'unknown' }
}
